import {FeatureCollection} from '@turf/turf';
import {
  getCameraBoundingBox,
  getFontSizeRenderByCanvasRatio,
  toIGNBBoxString,
  updateDataSourceLabelSize
} from '../../cesiumTools';
import {LayerDatasource, LayerLoadType, MapperViewer} from '../../types';
import {capitalize} from '../../../tools';

export class PlacesLayer implements LayerDatasource {

  id = 'places';
  name = 'Toponymes';
  active = false;
  dataSource = new Cesium.GeoJsonDataSource(this.name);
  loadType = LayerLoadType.Manual;
  viewer: MapperViewer;
  showLabel = false;
  isLoading = false;

  url = 'https://data.geopf.fr/wfs?version=2.0.0&SERVICE=WFS&REQUEST=GetFeature&OUTPUTFORMAT=json&TYPENAME=BDTOPO_V3:toponymie&BBOX=';

  previouslyLoadedBbox = null;

  constructor() {}

  onBeforePrint(): void {
    updateDataSourceLabelSize(this.dataSource, this.viewer);
  }
  onAfterPrint(): void {
    updateDataSourceLabelSize(this.dataSource, this.viewer);
  }

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;
    viewer.dataSources.add(this.dataSource);
  }

  remove(): void {
    this.viewer.dataSources.remove(this.dataSource);
  }

  loadData(forceReload = false): void {
    if (!this.dataSource) {
      console.error('Viewer not initialized');
      return;
    }

    const bbox = toIGNBBoxString(getCameraBoundingBox(this.viewer));

    if (this.previouslyLoadedBbox === bbox && !forceReload) {
      return;
    }

    this.previouslyLoadedBbox = bbox;

    if (bbox) {
      this.isLoading = true;

      const url = `${this.url}${bbox}`;
      fetch(url)
        .then(res => res.json())
        .then((geojson: FeatureCollection) => {
          this.buildPlaces(geojson);
        }).catch(error => {
          console.error('[PlacesLayer] -> (error) -> ', error);
          this.isLoading = false;
        });
    }
  }

  buildPlaces(res: any): void {
    if (res?.features.length === 0) {
      this.isLoading = false;

      return;
    }

    const textStyle = getFontSizeRenderByCanvasRatio(this.viewer);

    for (let i = 0; i < res.features.length; i++) {
      if (this.dataSource.entities.getById(`place_${res.features[i].properties.graphie_du_toponyme}`)) {
        continue;
      }

      if ('coordinates' in res.features[i].geometry) {
        this.dataSource.entities.add({
          id: `place_${res.features[i].properties.graphie_du_toponyme}`,
          position: Cesium.Cartesian3.fromDegrees(
            res.features[i].geometry.coordinates[0],
            res.features[i].geometry.coordinates[1]
          ),
          label: {
            text: capitalize(res.features[i].properties.graphie_du_toponyme),
            disableDepthTestDistance: 2000,
            fillColor: Cesium.Color.GREEN,
            backgroundColor: Cesium.Color.WHITE,
            showBackground: true,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            eyeOffset: new Cesium.Cartesian3(0, 0, -200),
            ...textStyle
          },
        });
      }
    }

    this.setActive(true);
    this.isLoading = false;
  }

  updateLabelScale(scale: number): void {
    this.dataSource.entities.values.forEach((entity) => {
      if (entity.label) {
        entity.label.scale = scale;
      }
    });
  }

  setActive(active: boolean): void {
    this.active = active;
    this.dataSource.show = active;

    if (active) {
      this.loadData();
    }
  }
}
